// src/services/requestStamp.ts
// ----------------------------
// The ONE place that decides what `requests.updated_by` / `updated_by_role` are
// set to. Every write to a request that wants to record "who touched this last"
// must use this helper rather than writing the two columns by hand.
//
// WHY IT EXISTS (23 Aug 2026). The two columns were being written at seven call
// sites and got it wrong three different ways:
//   * CommentPanel and useFileUploads hard-coded "shelter" whoever was acting, so
//     a ZEPHYR comment recorded itself as "Updated by you" on the DFV Service's
//     own list;
//   * four other writers set `updated_by` but not the role, leaving a stale role
//     from whoever wrote last — sometimes weeks earlier;
//   * nothing tied the two columns together, so they could disagree.
// One helper means the next person to add a write point cannot forget half of it.
//
// NOTE ON THE VALUES. `updated_by_role` is NOT the user's role — it is a two-value
// marker for WHICH SIDE acted. Every internal role (staff / admin / superuser /
// picker / combined) stamps "staff", because the only reader, ReqTable, asks
// "Zephyr or the service?" and shows "Updated by Zephyr" vs "Updated by you".
//
// `updated_by` deliberately keeps reading `user.shelterName`, unchanged. On a
// STAFF session that already resolves to the person's display name — sessionUser.ts
// falls through `shelterRow?.shelter_name || profile.display_name || email`, and a
// staff user has no shelter row — so it is already a person's name where it is
// read. Switching it to `displayName` would change what is STORED on shelter-side
// writes (service name → person name) for no reader's benefit, so it is left alone.

import { isInternalUser } from "../app/access";
import { ROLES } from "../utils/enums";

// The fields any write-point needs: enough to resolve the acting side and a label.
export interface StampUser {
  role?: string | null;
  shelterName?: string | null;
}

export interface RequestStamp {
  updated_by: string;
  updated_by_role: string;
}

// Spread into a `requests` update: { ...requestStamp(user), notes, ... }.
//
// A user object with no role resolves to the DFV Service side. That is the same
// default the hand-written call sites had, and it is the safe direction: a shelter
// user can never be mislabelled as Zephyr. Pinned by a test so it stays deliberate.
export function requestStamp(user: StampUser | null | undefined): RequestStamp {
  return {
    updated_by: user?.shelterName || "",
    updated_by_role: isInternalUser(user as never) ? ROLES.STAFF : ROLES.SHELTER,
  };
}
